import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Button,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as DoneIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ExecutionStep } from './ExecutionStep';
import { AnimatedProgressBar } from './AnimatedProgressBar';
import { isStepValueComplete } from '../../../../procedures/utils/stepCompletion';

function formatDateTime(dateString) {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
  } catch {
    return dateString;
  }
}

// Responses are stored as strings; structured step types (tables, files,
// multi-select) are JSON and need decoding before the step can show them.
function parseResponseValue(step) {
  const raw = step.response_value;
  if (typeof raw !== 'string' || step.step_type === 'TEXT_INPUT') return raw;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return raw;
  try {
    return JSON.parse(trimmed);
  } catch {
    return raw;
  }
}

export function ExecutionAccordion({
  execution,
  isExpanded,
  stepValues,
  submittingSteps,
  savedSteps,
  answeredSteps,
  procedureLoading,
  procedureName,
  taskId,
  onAccordionChange,
  onStepValueChange,
  onCompleteExecution,
  readOnly = false,
}) {
  const { t } = useTranslation(['tasks', 'common']);
  const isInProgress = execution.status === 'in_progress';
  const steps = useMemo(() => execution.steps || [], [execution.steps]);
  // Parsed once per load: a fresh object every render would re-render every
  // memoized step on each keystroke
  const savedValues = useMemo(
    () => Object.fromEntries(steps.map(s => [s.step_id, s.response_id !== null ? parseResponseValue(s) : undefined])),
    [steps],
  );
  const valueOf = (s) => {
    const key = `${execution.execution_id}_${s.step_id}`;
    return stepValues[key] !== undefined ? stepValues[key] : savedValues[s.step_id];
  };
  // Saved answers are tracked locally, so the task isn't reloaded per save.
  // While the run is open, tables and inspections must be filled in
  // completely; a finished run shows what was recorded.
  const isAnswered = (s) => (
    (s.response_id !== null || answeredSteps.has(`${execution.execution_id}_${s.step_id}`))
    && (!isInProgress || isStepValueComplete(s, valueOf(s)))
  );
  const answeredCount = steps.filter(isAnswered).length;
  const missingRequired = steps.filter((s) => s.is_required && !isAnswered(s));
  const isSaving = submittingSteps.size > 0;
  // Executions open with the task; nobody has started one until the first answer
  const isStarted = Boolean(execution.started_by);

  return (
    <Accordion
      expanded={isExpanded}
      onChange={onAccordionChange}
      disableGutters
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ width: '100%', pr: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            {isInProgress && !isStarted && (
              <Chip size="small" variant="outlined" label={t('tasks:procedure.status_not_started', 'Ikke påbegyndt')} />
            )}
            {isInProgress && isStarted && (
              <Chip size="small" color="primary" label={t('tasks:procedure.status_in_progress', 'I gang')} />
            )}
            {!isInProgress && (
              <Chip size="small" color="success" icon={<DoneIcon />} label={t('tasks:procedure.status_completed', 'Fuldført')} />
            )}
            <Typography variant="caption" color="text.secondary">
              {isInProgress && !isStarted && t('tasks:procedure.autosave_hint', 'Svar gemmes automatisk')}
              {isInProgress && isStarted && t('tasks:procedure.started_line', 'Startet {{date}} af {{name}}', {
                date: formatDateTime(execution.started_at),
                name: execution.started_by_name || '-',
              })}
              {!isInProgress && t('tasks:procedure.completed_line', 'Fuldført {{date}} af {{name}}', {
                date: formatDateTime(execution.completed_at),
                name: execution.started_by_name || '-',
              })}
            </Typography>
          </Box>
          <AnimatedProgressBar
            completedSteps={answeredCount}
            totalSteps={execution.total_steps}
            label={t('tasks:procedure.steps_answered', 'Besvarede trin')}
          />
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={1.5}>
          {steps.map((step) => {
            const key = `${execution.execution_id}_${step.step_id}`;
            const stepValue = valueOf(step);

            return (
              <ExecutionStep
                key={step.step_id}
                step={step}
                execution={execution}
                stepValue={stepValue}
                isSubmitting={submittingSteps.has(key)}
                isSaved={savedSteps.has(key)}
                isAnswered={isAnswered(step)}
                onStepValueChange={onStepValueChange}
                procedureName={procedureName}
                taskId={taskId}
                readOnly={readOnly}
              />
            );
          })}
        </Stack>

        {isInProgress && !readOnly && (
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {missingRequired.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('tasks:procedure.missing_required', {
                  count: missingRequired.length,
                  defaultValue: '{{count}} påkrævede trin mangler',
              })}
              </Typography>
            )}
            <Button
              variant="contained"
              color="success"
              onClick={() => onCompleteExecution(execution.execution_id)}
              disabled={procedureLoading || isSaving || missingRequired.length > 0}
            >
              {t('tasks:procedure.complete_execution', 'Fuldfør procedure')}
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
